import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CampaignIcon from '@mui/icons-material/Campaign'
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import API from '../../services/API'
import { DataGrid } from '@mui/x-data-grid'

export const AUDIENCE_OPTIONS = [
  { value: 'siteAdmins', label: 'Tenant site admins (all live tenants)' },
  { value: 'leads', label: 'All leads' },
  { value: 'mailingList', label: 'Mailing list leads only' },
  { value: 'allContacts', label: 'All contacts (incl. tenant customers)' },
]

export const CAMPAIGN_TYPE_OPTIONS = [
  {
    value: 'builtIn',
    label: "What's New announcement (built-in)",
    description:
      'A polished, branded email built from a simple list of feature titles — no Postmark template needed.',
  },
  {
    value: 'postmark',
    label: 'Postmark template',
    description: 'Send a template saved on the Postmark broadcast server.',
  },
]

export const audienceLabel = (value) =>
  AUDIENCE_OPTIONS.find((option) => option.value === value)?.label ?? value

export const STATUS_CHIP_COLORS = {
  draft: 'default',
  sending: 'warning',
  sent: 'success',
  failed: 'error',
}

const dataGridSx = {
  border: '1px solid #E2E8F0',
  borderRadius: 3,
  overflow: 'hidden',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#F8FAFC',
    color: '#64748B',
    fontWeight: 900,
  },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
    borderColor: '#EEF2F7',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#F8FAFC',
  },
}

export default function Campaigns() {
  const navigate = useNavigate()
  const [campaigns, setCampaigns] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [templateForm, setTemplateForm] = useState({ name: '', postmarkTemplateId: '' })
  const [templateSaving, setTemplateSaving] = useState(false)

  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false)
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    type: 'builtIn',
    templateId: '',
    audience: 'siteAdmins',
  })
  const [campaignSaving, setCampaignSaving] = useState(false)

  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const loadData = async () => {
    setLoading(true)
    try {
      const [campaignsRes, templatesRes] = await Promise.all([
        API.get('/admincrm/email-campaigns'),
        API.get('/admincrm/email-templates'),
      ])
      if (campaignsRes.status === 200) setCampaigns(campaignsRes.data)
      if (templatesRes.status === 200) setTemplates(templatesRes.data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const createTemplate = async () => {
    setTemplateSaving(true)
    setError('')
    const res = await API.post('/admincrm/email-templates', {
      name: templateForm.name,
      postmarkTemplateId: Number(templateForm.postmarkTemplateId),
    })
    setTemplateSaving(false)
    if (res.status !== 200) {
      setError(res.data?.message ?? 'Failed to save the template')
      return
    }
    setTemplateDialogOpen(false)
    setTemplateForm({ name: '', postmarkTemplateId: '' })
    loadData()
  }

  const deleteTemplate = async (template) => {
    if (!window.confirm(`Delete the template "${template.name}"?`)) return
    const res = await API.delete(`/admincrm/email-templates/${template._id}`)
    if (res.status !== 200) {
      window.alert(res.data?.message ?? 'Failed to delete the template')
      return
    }
    loadData()
  }

  const previewTemplate = async (template) => {
    setPreview({ name: template.name })
    setPreviewLoading(true)
    const res = await API.get(`/admincrm/email-templates/${template._id}/preview`)
    setPreviewLoading(false)
    if (res.status !== 200) {
      setPreview(null)
      window.alert(res.data?.message ?? 'Failed to load the preview from Postmark')
      return
    }
    setPreview({ name: template.name, ...res.data })
  }

  const createCampaign = async () => {
    setCampaignSaving(true)
    setError('')
    const payload =
      campaignForm.type === 'builtIn'
        ? {
            name: campaignForm.name,
            type: 'builtIn',
            audience: campaignForm.audience,
          }
        : {
            name: campaignForm.name,
            templateId: campaignForm.templateId,
            audience: campaignForm.audience,
          }
    const res = await API.post('/admincrm/email-campaigns', payload)
    setCampaignSaving(false)
    if (res.status !== 200) {
      setError(res.data?.message ?? 'Failed to create the campaign')
      return
    }
    setCampaignDialogOpen(false)
    setCampaignForm({ name: '', type: 'builtIn', templateId: '', audience: 'siteAdmins' })
    navigate(`/campaigns/${res.data._id}`)
  }

  const deleteCampaign = async (campaign) => {
    if (!window.confirm(`Delete the draft campaign "${campaign.name}"?`)) return
    const res = await API.delete(`/admincrm/email-campaigns/${campaign._id}`)
    if (res.status !== 200) {
      window.alert(res.data?.message ?? 'Failed to delete the campaign')
      return
    }
    loadData()
  }

  const campaignColumns = [
    {
      field: 'name',
      headerName: 'Campaign',
      flex: 1.4,
      minWidth: 220,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <CampaignIcon color="secondary" fontSize="small" />
          <Typography fontWeight={800}>{row.name}</Typography>
        </Stack>
      ),
    },
    {
      field: 'templateName',
      headerName: 'Template',
      flex: 1,
      minWidth: 160,
      valueGetter: (value, row) =>
        row.type === 'builtIn'
          ? `${row.templateName ?? "What's New"} (built-in)`
          : row.template?.name ?? row.templateName ?? '',
    },
    {
      field: 'audience',
      headerName: 'Audience',
      flex: 1,
      minWidth: 180,
      valueFormatter: (value) => audienceLabel(value),
    },
    {
      field: 'recipientCount',
      headerName: 'Recipients',
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value) => value ?? '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Chip
          label={row.status}
          color={STATUS_CHIP_COLORS[row.status] ?? 'default'}
          variant={row.status === 'draft' ? 'outlined' : 'filled'}
          sx={{ textTransform: 'capitalize', fontWeight: 700 }}
        />
      ),
    },
    {
      field: 'sentAt',
      headerName: 'Sent',
      minWidth: 150,
      valueFormatter: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:mm') : '—',
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 110,
      sortable: false,
      filterable: false,
      disableExport: true,
      align: 'right',
      renderCell: ({ row }) => (
        <Stack direction="row">
          {row.status === 'draft' && (
            <Tooltip title="Delete draft">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  deleteCampaign(row)
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Open campaign">
            <IconButton onClick={() => navigate(`/campaigns/${row._id}`)}>
              <ArrowForwardIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  const templateColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      renderCell: ({ row }) => <Typography fontWeight={800}>{row.name}</Typography>,
    },
    {
      field: 'postmarkTemplateId',
      headerName: 'Postmark ID',
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Chip label={row.postmarkTemplateId} variant="outlined" />
      ),
    },
    {
      field: 'subject',
      headerName: 'Subject',
      flex: 1.6,
      minWidth: 240,
    },
    {
      field: 'createdAt',
      headerName: 'Added',
      minWidth: 130,
      valueFormatter: (value) =>
        value ? moment(value).format('DD/MM/YYYY') : '',
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 110,
      sortable: false,
      filterable: false,
      disableExport: true,
      align: 'right',
      renderCell: ({ row }) => (
        <Stack direction="row">
          <Tooltip title="Preview template">
            <IconButton onClick={() => previewTemplate(row)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete template">
            <IconButton onClick={() => deleteTemplate(row)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 5,
          overflow: 'hidden',
          border: '1px solid #E2E8F0',
          boxShadow: '0 18px 60px rgba(36,47,64,0.12)',
        }}
      >
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 52%, #239AA4 150%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.72 }}>
                AmbuNet CRM
              </Typography>
              <Typography variant="h4" fontWeight={900} color="white">
                Email Campaigns
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.78)', maxWidth: 720 }}
              >
                Send a built-in &ldquo;What&apos;s New&rdquo; announcement to
                tenant site admins, or a saved Postmark template to your sales
                leads — preview everything before it goes out via the Postmark
                broadcast stream.
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
            >
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setTemplateDialogOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 900,
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: 'white' },
                }}
              >
                Add template
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCampaignDialogOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 900,
                  bgcolor: '#239AA4',
                  '&:hover': { bgcolor: '#1D858E' },
                }}
              >
                New campaign
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" fontWeight={900} mb={1}>
                Campaigns
              </Typography>
              <DataGrid
                rows={campaigns}
                columns={campaignColumns}
                loading={loading}
                getRowId={(row) => row._id}
                autoHeight
                rowHeight={64}
                columnHeaderHeight={56}
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={dataGridSx}
              />
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={900} mb={1}>
                Postmark Templates
              </Typography>
              <DataGrid
                rows={templates}
                columns={templateColumns}
                loading={loading}
                getRowId={(row) => row._id}
                autoHeight
                rowHeight={64}
                columnHeaderHeight={56}
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={dataGridSx}
              />
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Postmark template</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template name"
              value={templateForm.name}
              onChange={(e) =>
                setTemplateForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="e.g. August product update"
              fullWidth
              required
            />
            <TextField
              label="Postmark template ID"
              type="number"
              value={templateForm.postmarkTemplateId}
              onChange={(e) =>
                setTemplateForm((prev) => ({
                  ...prev,
                  postmarkTemplateId: e.target.value,
                }))
              }
              placeholder="e.g. 41367421"
              fullWidth
              required
            />
            <Alert severity="info">
              The template must exist on the Postmark <strong>broadcast</strong>{' '}
              server — it will be verified when you save. Available template
              variables: <code>{'{{firstName}}'}</code>, <code>{'{{name}}'}</code>,{' '}
              <code>{'{{email}}'}</code>, <code>{'{{organisationName}}'}</code>.
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!templateForm.name || !templateForm.postmarkTemplateId || templateSaving}
            onClick={createTemplate}
          >
            {templateSaving ? 'Verifying…' : 'Save template'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={campaignDialogOpen}
        onClose={() => setCampaignDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New campaign</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Campaign name"
              value={campaignForm.name}
              onChange={(e) =>
                setCampaignForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="e.g. August 2026 product update"
              fullWidth
              required
            />
            <TextField
              select
              label="Campaign type"
              value={campaignForm.type}
              onChange={(e) =>
                setCampaignForm((prev) => ({
                  ...prev,
                  type: e.target.value,
                  audience: e.target.value === 'builtIn' ? 'siteAdmins' : 'leads',
                }))
              }
              fullWidth
            >
              {CAMPAIGN_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {campaignForm.type === 'builtIn' ? (
              <Alert severity="info">
                You&apos;ll list a title and short description for each new
                feature on the next screen — AmbuNet builds the fancy email for
                you.
              </Alert>
            ) : (
              <TextField
                select
                label="Postmark template"
                value={campaignForm.templateId}
                onChange={(e) =>
                  setCampaignForm((prev) => ({ ...prev, templateId: e.target.value }))
                }
                fullWidth
                required
              >
                {templates.map((template) => (
                  <MenuItem key={template._id} value={template._id}>
                    {template.name} (#{template.postmarkTemplateId})
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select
              label="Audience"
              value={campaignForm.audience}
              onChange={(e) =>
                setCampaignForm((prev) => ({ ...prev, audience: e.target.value }))
              }
              fullWidth
            >
              {AUDIENCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Alert severity="info">
              Nothing is sent yet — the campaign is created as a draft where you
              can preview it and review the recipient list before sending.
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !campaignForm.name ||
              (campaignForm.type === 'postmark' && !campaignForm.templateId) ||
              campaignSaving
            }
            onClick={createCampaign}
          >
            Create draft
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Preview — {preview?.name}</DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Stack alignItems="center" py={6}>
              <CircularProgress />
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Typography>
                <strong>Subject:</strong> {preview?.subject}
              </Typography>
              <Box
                component="iframe"
                title="Email preview"
                srcDoc={preview?.htmlBody ?? preview?.textBody ?? ''}
                sandbox=""
                sx={{
                  width: '100%',
                  height: '60vh',
                  border: '1px solid #E2E8F0',
                  borderRadius: 2,
                  bgcolor: 'white',
                }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
